import {
  SegmentAuthAction,
  SegmentAuthFailure,
  SegmentAuthResult,
  type SegmentAuthApiError,
  type SegmentAuthOutcome,
} from '@/services/db/segment-in-integration/mutations';
import { parseSegmentSourceName } from './connection-status';

/**
 * What the banner says after an attempt.
 *
 * The decision is kept apart from the rendering because the interesting part is
 * which of nine outcomes applies and which names it uses — the markup is one
 * banner either way. The `source already connected` family in particular splits
 * three ways on the error's `meta` alone, and those three are separate acceptance
 * tests.
 */
export const SegmentAlertKind = {
  Verified: 'verified',
  Connected: 'connected',
  ReEnabled: 're-enabled',
  Disabled: 'disabled',
  Deleted: 'deleted',
  WorkspaceDisabled: 'workspace-disabled',
  WorkspaceNotFound: 'workspace-not-found',
  /** The popup was closed, blocked, or Segment no longer authorizes the reader. */
  AuthorizationFailed: 'authorization-failed',
  /** The wait for the popup ran out, which says nothing about the connection. */
  WaitTimedOut: 'wait-timed-out',
  /** The chosen Segment workspace already has a Customer.io destination. */
  DestinationExists: 'destination-exists',
  SourceMismatch: 'source-mismatch',
  SourceAlreadyConnected: 'source-already-connected',
  /** Anything else the API refused with, which no other kind explains. */
  AttemptFailed: 'attempt-failed',
} as const;

export type SegmentAlertKind = (typeof SegmentAlertKind)[keyof typeof SegmentAlertKind];

export type SegmentAlertDescriptor =
  | { kind: Exclude<SegmentAlertKind, 'source-mismatch' | 'source-already-connected'> }
  | {
      kind: 'source-mismatch';
      /** The source already connected, which the reader must pick instead. */
      existingSource: string;
      /** What they picked in Segment this time. */
      chosenSource: string;
      /** The action they were attempting, which names the control to retry with. */
      action: SegmentAuthAction;
    }
  | {
      kind: 'source-already-connected';
      source: string;
      /** The workspace that holds it, when it is one this reader can be told about. */
      otherWorkspaceId: string | null;
      /** Set when the clash is a credential rather than a workspace. */
      otherSiteId: string | null;
    };

const SOURCE_MISMATCH = 'source mismatch';
const DESTINATION_MISMATCH = 'destination mismatch';

function sourceMismatchAlert(error: SegmentAuthApiError, action: SegmentAuthAction): SegmentAlertDescriptor {
  return {
    kind: SegmentAlertKind.SourceMismatch,
    existingSource: parseSegmentSourceName(error.meta?.existing_source_name) ?? '',
    chosenSource: parseSegmentSourceName(error.meta?.new_source_name) ?? '',
    action,
  };
}

/**
 * Three ways a destination can already be taken, distinguished only by `meta`.
 *
 * No `environment_id` means some workspace holds it that this reader cannot be
 * told about. A different one names that workspace. The same one means the clash
 * is the credential, not the workspace.
 */
function destinationMismatchAlert(
  error: SegmentAuthApiError,
  currentWorkspaceId: string,
  currentSiteId: string | null,
): SegmentAlertDescriptor {
  const source = parseSegmentSourceName(error.meta?.source) ?? '';
  const environmentId = error.meta?.environment_id?.toString() ?? null;
  const siteId = error.meta?.site_id ?? null;

  if (environmentId == null) {
    return { kind: SegmentAlertKind.SourceAlreadyConnected, source, otherWorkspaceId: null, otherSiteId: null };
  }

  if (environmentId !== currentWorkspaceId) {
    return {
      kind: SegmentAlertKind.SourceAlreadyConnected,
      source,
      otherWorkspaceId: environmentId,
      otherSiteId: null,
    };
  }

  if (siteId !== currentSiteId) {
    return { kind: SegmentAlertKind.SourceAlreadyConnected, source, otherWorkspaceId: null, otherSiteId: siteId };
  }

  return { kind: SegmentAlertKind.SourceAlreadyConnected, source, otherWorkspaceId: null, otherSiteId: null };
}

/**
 * What a failed attempt says, which is always something.
 *
 * The named errors get their own copy; everything else — a 500, a detail nothing
 * recognises, a body with no `errors` at all — falls back to the generic banner.
 * Ember handed those to its error service, which reported them; this screen has no
 * snackbar, so returning nothing here would leave the reader with a spinner and
 * then silence.
 */
function erroredAlert(
  outcome: SegmentAuthOutcome,
  currentWorkspaceId: string,
  currentSiteId: string | null,
): SegmentAlertDescriptor {
  const first = outcome.errors?.[0];

  if (first != null) {
    if (first.detail === SOURCE_MISMATCH) return sourceMismatchAlert(first, outcome.action);
    if (first.detail === DESTINATION_MISMATCH) {
      return destinationMismatchAlert(first, currentWorkspaceId, currentSiteId);
    }
    if (first.status === '404') return { kind: SegmentAlertKind.WorkspaceNotFound };
  }

  return { kind: SegmentAlertKind.AttemptFailed };
}

export type AlertInput = {
  outcome: SegmentAuthOutcome;
  /** Set when the attempt threw, which is the only way some failures are known. */
  failure?: SegmentAuthFailure | null;
  currentWorkspaceId: string;
  /** The credential the attempt used, for telling a credential clash apart. */
  currentSiteId: string | null;
};

/**
 * The banner for one attempt. Every attempt gets one.
 *
 * A verify that succeeds says so explicitly, which is why `Created` splits on the
 * action: the same result means "connected" for a create and "verified" for a
 * check. The return type is not nullable on purpose — an attempt the screen cannot
 * explain still has to be reported, so a branch that returns nothing is a
 * compile error rather than a silent failure.
 */
export function deriveConnectionAlert({
  outcome,
  failure,
  currentWorkspaceId,
  currentSiteId,
}: AlertInput): SegmentAlertDescriptor {
  // These are known only from the thrown failure, not from an outcome to read.
  if (failure === SegmentAuthFailure.Authorization) return { kind: SegmentAlertKind.AuthorizationFailed };
  // A wait that ran out is not a refused authorization. The reader may have been
  // consenting the whole time, so the banner says only that we stopped waiting.
  if (failure === SegmentAuthFailure.Timeout) return { kind: SegmentAlertKind.WaitTimedOut };
  if (failure === SegmentAuthFailure.DestinationExists) return { kind: SegmentAlertKind.DestinationExists };

  if (outcome.state === SegmentAuthResult.Created && outcome.action === SegmentAuthAction.Check) {
    return { kind: SegmentAlertKind.Verified };
  }

  switch (outcome.state) {
    case SegmentAuthResult.Created:
      return { kind: SegmentAlertKind.Connected };
    case SegmentAuthResult.Enabled:
      return { kind: SegmentAlertKind.ReEnabled };
    case SegmentAuthResult.Disabled:
      return { kind: SegmentAlertKind.Disabled };
    case SegmentAuthResult.Deleted:
      return { kind: SegmentAlertKind.Deleted };
    case SegmentAuthResult.Errored:
      break;
    default: {
      const exhaustive: never = outcome.state;
      return exhaustive;
    }
  }

  // A destination that answered but reports itself off is not an error to explain,
  // it is a workspace the reader has to re-enable in Segment.
  if (outcome.enabled === true) return { kind: SegmentAlertKind.Verified };
  if (outcome.enabled === false) return { kind: SegmentAlertKind.WorkspaceDisabled };

  return erroredAlert(outcome, currentWorkspaceId, currentSiteId);
}

const ERROR_KINDS: readonly SegmentAlertKind[] = [
  SegmentAlertKind.AuthorizationFailed,
  SegmentAlertKind.WorkspaceDisabled,
  SegmentAlertKind.WorkspaceNotFound,
  SegmentAlertKind.SourceMismatch,
  SegmentAlertKind.SourceAlreadyConnected,
  SegmentAlertKind.AttemptFailed,
];

/**
 * The banner's colour and icon.
 *
 * A wait that ran out is a caution rather than a failure: nothing was changed, and
 * the reader only has to try again, so a critical banner would contradict its own
 * copy.
 */
export function bannerLook(kind: SegmentAlertKind) {
  if (kind === SegmentAlertKind.WaitTimedOut) return { variant: 'caution', icon: 'caution' } as const;
  if (ERROR_KINDS.includes(kind)) return { variant: 'critical', icon: 'critical' } as const;

  return { variant: 'success', icon: 'check-circle' } as const;
}
